import { Dispatch, useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';

import { usePaymentTrackerOnDemand } from '@api';
import { EPaymentProvider, ETrackerPaymentAction, ETrackerPaymentMessage, ICartPrice } from '@model';
import { ECartType, EcommerceSectionNamespace, trackBeginCheckout } from '@services/ecommerceService';
import { ELogLevel } from '@services/errorService';
import { useErrorContext } from '@services/errorService';
import { nonAwaitedPromiseHandlerParametrized } from '@utils/async';
import { PAYPAL_EXPRESS_PAYMENT_ID } from '@utils/constants';

import { useCartPageContext } from '../../../context';
import { createEcommerceCheckout } from '../../../ecommerce';
import { IProduct, IServices } from '../../../model';
import { useGetPayPalOrder, usePostPayPalOrder } from '../../../services';
import { messages } from '../messages';

import { EPayPalOrderAction, IPayPalOrderActions, IPayPalOrderUseActions } from './model';

export const useActions = (dispatch: Dispatch<IPayPalOrderActions>): IPayPalOrderUseActions => {
  const productsRef = useRef<IProduct[]>();
  const pricesSummaryRef = useRef<ICartPrice>();
  const servicesRef = useRef<IServices>();

  const { request: createPayPalOrder } = usePostPayPalOrder();
  const { request: authorizePayPalOrder } = useGetPayPalOrder();
  const { request: paymentLoggerRequest } = usePaymentTrackerOnDemand();

  const {
    state: { products, pricesSummary, serviceItems },
  } = useCartPageContext();

  const { callLogger, setErrorState } = useErrorContext();
  const { formatMessage } = useIntl();

  useEffect(() => {
    productsRef.current = products;
    pricesSummaryRef.current = pricesSummary;
    servicesRef.current = serviceItems;
  }, [products, pricesSummary, serviceItems]);

  return useMemo(
    () => ({
      handleOrderCreate: async () => {
        nonAwaitedPromiseHandlerParametrized(paymentLoggerRequest, {
          orderId: 0,
          paymentId: PAYPAL_EXPRESS_PAYMENT_ID,
          action: ETrackerPaymentAction.BUTTON_CLICK,
          provider: EPaymentProvider.PAYPAL,
          message: ETrackerPaymentMessage.BUTTON_CLICK,
        });

        if (productsRef.current && pricesSummaryRef.current && servicesRef.current) {
          trackBeginCheckout(
            { ...createEcommerceCheckout(productsRef.current, pricesSummaryRef.current, servicesRef.current) },
            ECartType.Cart,
            EcommerceSectionNamespace.EEcommerceInteraction.BUTTON_PAYPAL,
          );
        }

        const result = await createPayPalOrder();
        if (result) {
          dispatch({
            actionType: EPayPalOrderAction.CREATE,
            payload: { orderId: result.payPalOrderId },
          });

          return result.payPalOrderId;
        }

        return null;
      },
      handleOrderApprove: async (params) => {
        const result = await authorizePayPalOrder(params.orderID);
        if (result?.data) {
          return dispatch({
            actionType: EPayPalOrderAction.APPROVE,
            payload: {
              billingToken: params.billingToken,
              facilitatorAccessToken: params.facilitatorAccessToken,
              payerId: params.payerID,
              status: result.data.orderStatus,
              customer: result.data.customer,
              deliveryAddress: result.data.deliveryAddress,
            },
          });
        }
        setErrorState({ messages: [formatMessage(messages.communicationError)] });
        return dispatch({ actionType: EPayPalOrderAction.ERROR });
      },
      handlePayPalError: (error: Error) => {
        dispatch({ actionType: EPayPalOrderAction.ERROR });

        callLogger({
          logLevel: ELogLevel.ERROR,
          logError: new Error(
            `Error has occurred during paypal express checkout button init. [Error: ${error.message}]`,
          ),
        });

        setErrorState({ messages: [formatMessage(messages.communicationError)] });
      },
    }),
    [setErrorState, callLogger, authorizePayPalOrder, createPayPalOrder, dispatch, formatMessage, paymentLoggerRequest],
  );
};
